// ─────────────────────────────────────────────────────────────────────────────
// slipwash.js — Slip (SL) and Wash (WA).
//
// theoretical_CA = -arccos(SP / OL)        // signed negative (catch side)
// theoretical_FA = +arccos(SP / OL)        // signed positive (finish side)
// SL = theoretical_CA - actual_CA          // positive => blade entered late
// WA = actual_FA - theoretical_FA          // positive => blade extracted late
//
// SP (Spread, cm) and OL (Oar Length, cm) come from the rigging input.
// All angles are in degrees. Returns null if SP or OL are missing/invalid,
// or if SP >= OL (geometrically impossible — arccos undefined).
// ─────────────────────────────────────────────────────────────────────────────

const RAD_TO_DEG = 180 / Math.PI;

/**
 * Theoretical catch/finish angles derived from rig geometry.
 * @param {{ ol: number, sp: number }} rigging — OL & SP in cm.
 */
export function theoreticalAngles({ ol, sp } = {}) {
  if (typeof ol !== 'number' || typeof sp !== 'number') return null;
  if (ol <= 0 || sp <= 0) return null;
  const ratio = sp / ol;
  if (ratio >= 1) return null; // arccos undefined / oar too short for spread
  const magDeg = Math.acos(ratio) * RAD_TO_DEG;
  return { theoretical_CA: -magDeg, theoretical_FA: +magDeg };
}

/**
 * Slip (SL) — degrees. Positive => blade entered late.
 */
export function slip(actualCA, rigging) {
  if (typeof actualCA !== 'number') return null;
  const t = theoreticalAngles(rigging);
  if (!t) return null;
  return t.theoretical_CA - actualCA;
}

/**
 * Wash (WA) — degrees. Positive => blade extracted late.
 */
export function wash(actualFA, rigging) {
  if (typeof actualFA !== 'number') return null;
  const t = theoreticalAngles(rigging);
  if (!t) return null;
  return actualFA - t.theoretical_FA;
}

/**
 * Convenience: returns { SL, WA, theoretical_CA, theoretical_FA } in one call.
 */
export function slipWashMetrics({ actualCA, actualFA, rigging }) {
  const t = theoreticalAngles(rigging);
  if (!t) return { SL: null, WA: null, theoretical_CA: null, theoretical_FA: null };
  const SL = (typeof actualCA === 'number') ? t.theoretical_CA - actualCA : null;
  const WA = (typeof actualFA === 'number') ? actualFA - t.theoretical_FA : null;
  return { SL, WA, ...t };
}
